import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { number, object, string } from "yup";

import { api, apiHooks } from "~/api.js";
import {
  Button,
  FormLabel,
  HelperText,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";

type XeroConfigResponse = {
  clientId: string;
  tenantId: string;
  enabled: boolean;
  autoCreateContacts: boolean;
  configuredRateAccountCode: string;
  mileageAccountCode: string;
  otherAccountCode: string;
  dueDateDays: number;
  maxPaymentTotalPence: number;
  maxRequestsPerDoctorPerDay: number;
  maxRequestsPerPatientPerDay: number;
  maxOtherAmountPence: number;
  maxMileageAmountPence: number;
  isConnected: boolean;
};

type XeroFormValues = {
  clientId: string;
  clientSecret: string;
  tenantId: string;
  enabled: boolean;
  autoCreateContacts: boolean;
  configuredRateAccountCode: string;
  mileageAccountCode: string;
  otherAccountCode: string;
  dueDateDays: number;
  maxPaymentTotalPence: number;
  maxRequestsPerDoctorPerDay: number;
  maxRequestsPerPatientPerDay: number;
  maxOtherAmountPence: number;
  maxMileageAmountPence: number;
};

const xeroConfigurationSchema = object({
  clientId: string().required("Client ID is required"),
  clientSecret: string().default(""),
  tenantId: string().required("Tenant ID is required"),
  configuredRateAccountCode: string().required("Account code is required"),
  mileageAccountCode: string().required("Account code is required"),
  otherAccountCode: string().required("Account code is required"),
  dueDateDays: number()
    .min(1, "Must be at least 1 day")
    .required("Due date days is required"),
  maxPaymentTotalPence: number()
    .min(0, "Must be 0 or greater")
    .required("Maximum payment total is required"),
  maxRequestsPerDoctorPerDay: number()
    .min(1, "Must be at least 1")
    .required("Maximum requests per doctor per day is required"),
  maxRequestsPerPatientPerDay: number()
    .min(1, "Must be at least 1")
    .required("Maximum requests per patient per day is required"),
  maxOtherAmountPence: number()
    .min(0, "Must be 0 or greater")
    .required("Maximum other amount is required"),
  maxMileageAmountPence: number()
    .min(0, "Must be 0 or greater")
    .required("Maximum mileage amount is required"),
});

function penceToPounds(pence: number): string {
  return (pence / 100).toFixed(2);
}

function XeroConfigurationInner({
  config,
  reload,
}: {
  config: XeroConfigResponse;
  reload: () => void;
}) {
  return (
    <Formik<XeroFormValues>
      initialValues={{
        clientId: config.clientId,
        clientSecret: "",
        tenantId: config.tenantId,
        enabled: config.enabled,
        autoCreateContacts: config.autoCreateContacts,
        configuredRateAccountCode: config.configuredRateAccountCode,
        mileageAccountCode: config.mileageAccountCode,
        otherAccountCode: config.otherAccountCode,
        dueDateDays: config.dueDateDays,
        maxPaymentTotalPence: config.maxPaymentTotalPence,
        maxRequestsPerDoctorPerDay: config.maxRequestsPerDoctorPerDay,
        maxRequestsPerPatientPerDay: config.maxRequestsPerPatientPerDay,
        maxOtherAmountPence: config.maxOtherAmountPence,
        maxMileageAmountPence: config.maxMileageAmountPence,
      }}
      validationSchema={xeroConfigurationSchema}
      onSubmit={async (values) => {
        const payload: Record<string, unknown> = { ...values };
        // Only send clientSecret if the user actually typed a new one
        if (values.clientSecret === "") {
          delete payload.clientSecret;
        }

        const result = await api.configuration.updateXeroConfiguration(payload);

        if (result.status === 204) {
          renderSuccessToast({ message: "Xero configuration updated" });
          reload();
        } else {
          renderErrorToast({
            message: "Failed to update Xero configuration",
          });
        }
      }}
    >
      {(formikData) => {
        const { values, errors, touched, submitForm } = formikData;

        const updateField = (
          field: keyof XeroFormValues,
          value: string | number | boolean,
        ) => {
          void formikData.setValues({ ...values, [field]: value });
        };

        return (
          <Box
            sx={{
              maxWidth: "600px",
              marginBottom: "2em",
              gap: "1em",
              mt: "2em",
            }}
          >
            <Typography variant="h5">Xero Configuration</Typography>

            <Alert
              severity={
                config.isConnected && values.enabled ? "success" : "warning"
              }
              sx={{ mt: "1em", mb: "1em" }}
            >
              {!values.enabled
                ? "Xero integration is disabled. Toggle the switch below to enable auto-billing."
                : config.isConnected
                  ? "Xero is connected. Auto-billing is active for eligible payment requests."
                  : "Xero is not connected. Enter Client ID and Tenant ID to enable auto-billing."}
            </Alert>

            <FormControlLabel
              control={
                <Switch
                  checked={values.enabled}
                  onChange={() => {
                    updateField("enabled", !values.enabled);
                  }}
                />
              }
              label="Enable Xero Integration"
              sx={{ mb: "1em" }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={values.autoCreateContacts}
                  onChange={() => {
                    updateField(
                      "autoCreateContacts",
                      !values.autoCreateContacts,
                    );
                  }}
                />
              }
              label="Auto-create Xero contacts for doctors"
              sx={{ mb: "1em" }}
            />
            {!values.autoCreateContacts && (
              <Alert severity="info" sx={{ mb: "1em" }}>
                Doctors must be manually linked to Xero contacts before bills
                can be auto-created. Set a doctor&apos;s Xero Contact ID in User
                Management.
              </Alert>
            )}

            {/* ── Credentials ─────────────────────────────────── */}
            <Typography variant="h6" sx={{ mt: "1.5em" }}>
              Credentials
            </Typography>
            <Divider sx={{ mb: "0.5em" }} />

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label="Client ID (*)"
                error={touched.clientId && !!errors.clientId}
              />
              <TextField
                name="clientId"
                useFullWidth={true}
                value={values.clientId}
                onChange={(e: string) => {
                  updateField("clientId", e);
                }}
              />
              {errors.clientId && touched.clientId && (
                <HelperText errorMessage={errors.clientId} />
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label="Client Secret (leave blank to keep current)"
                error={touched.clientSecret && !!errors.clientSecret}
              />
              <TextField
                name="clientSecret"
                useFullWidth={true}
                value={values.clientSecret}
                onChange={(e: string) => {
                  updateField("clientSecret", e);
                }}
              />
              {errors.clientSecret && touched.clientSecret && (
                <HelperText errorMessage={errors.clientSecret} />
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label="Tenant ID (*)"
                error={touched.tenantId && !!errors.tenantId}
              />
              <TextField
                name="tenantId"
                useFullWidth={true}
                value={values.tenantId}
                onChange={(e: string) => {
                  updateField("tenantId", e);
                }}
              />
              {errors.tenantId && touched.tenantId && (
                <HelperText errorMessage={errors.tenantId} />
              )}
            </Box>

            {/* ── Account Codes ───────────────────────────────── */}
            <Typography variant="h6" sx={{ mt: "1.5em" }}>
              Account Codes
            </Typography>
            <Divider sx={{ mb: "0.5em" }} />

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label="Configured Rate Account Code (*)"
                error={
                  touched.configuredRateAccountCode &&
                  !!errors.configuredRateAccountCode
                }
              />
              <TextField
                name="configuredRateAccountCode"
                useFullWidth={true}
                value={values.configuredRateAccountCode}
                onChange={(e: string) => {
                  updateField("configuredRateAccountCode", e);
                }}
              />
              {errors.configuredRateAccountCode &&
                touched.configuredRateAccountCode && (
                  <HelperText errorMessage={errors.configuredRateAccountCode} />
                )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label="Mileage Account Code (*)"
                error={
                  touched.mileageAccountCode && !!errors.mileageAccountCode
                }
              />
              <TextField
                name="mileageAccountCode"
                useFullWidth={true}
                value={values.mileageAccountCode}
                onChange={(e: string) => {
                  updateField("mileageAccountCode", e);
                }}
              />
              {errors.mileageAccountCode && touched.mileageAccountCode && (
                <HelperText errorMessage={errors.mileageAccountCode} />
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label="Other Expenses Account Code (*)"
                error={touched.otherAccountCode && !!errors.otherAccountCode}
              />
              <TextField
                name="otherAccountCode"
                useFullWidth={true}
                value={values.otherAccountCode}
                onChange={(e: string) => {
                  updateField("otherAccountCode", e);
                }}
              />
              {errors.otherAccountCode && touched.otherAccountCode && (
                <HelperText errorMessage={errors.otherAccountCode} />
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label="Due Date (days after bill creation) (*)"
                error={touched.dueDateDays && !!errors.dueDateDays}
              />
              <TextField
                name="dueDateDays"
                useFullWidth={true}
                value={String(values.dueDateDays)}
                onChange={(e: string) => {
                  updateField("dueDateDays", parseInt(e, 10) || 0);
                }}
              />
              {errors.dueDateDays && touched.dueDateDays && (
                <HelperText errorMessage={errors.dueDateDays} />
              )}
            </Box>

            {/* ── Sanity Constraints ──────────────────────────── */}
            <Typography variant="h6" sx={{ mt: "1.5em" }}>
              Auto-Bill Sanity Constraints
            </Typography>
            <Typography variant="body2" sx={{ mb: "0.5em", color: "grey.600" }}>
              Payment requests that exceed these limits will not be auto-billed.
              They will still be accepted but will require manual billing.
            </Typography>
            <Divider sx={{ mb: "0.5em" }} />

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={`Maximum Payment Total (£${penceToPounds(values.maxPaymentTotalPence)}) (*)`}
                error={
                  touched.maxPaymentTotalPence && !!errors.maxPaymentTotalPence
                }
              />
              <TextField
                name="maxPaymentTotalPence"
                useFullWidth={true}
                value={String(values.maxPaymentTotalPence)}
                onChange={(e: string) => {
                  updateField("maxPaymentTotalPence", parseInt(e, 10) || 0);
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Value in pence. Current: £
                {penceToPounds(values.maxPaymentTotalPence)}
              </Typography>
              {errors.maxPaymentTotalPence && touched.maxPaymentTotalPence && (
                <HelperText errorMessage={errors.maxPaymentTotalPence} />
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label="Maximum Requests per Doctor per Day (*)"
                error={
                  touched.maxRequestsPerDoctorPerDay &&
                  !!errors.maxRequestsPerDoctorPerDay
                }
              />
              <TextField
                name="maxRequestsPerDoctorPerDay"
                useFullWidth={true}
                value={String(values.maxRequestsPerDoctorPerDay)}
                onChange={(e: string) => {
                  updateField(
                    "maxRequestsPerDoctorPerDay",
                    parseInt(e, 10) || 0,
                  );
                }}
              />
              {errors.maxRequestsPerDoctorPerDay &&
                touched.maxRequestsPerDoctorPerDay && (
                  <HelperText
                    errorMessage={errors.maxRequestsPerDoctorPerDay}
                  />
                )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label="Maximum Requests per Patient per Day (*)"
                error={
                  touched.maxRequestsPerPatientPerDay &&
                  !!errors.maxRequestsPerPatientPerDay
                }
              />
              <TextField
                name="maxRequestsPerPatientPerDay"
                useFullWidth={true}
                value={String(values.maxRequestsPerPatientPerDay)}
                onChange={(e: string) => {
                  updateField(
                    "maxRequestsPerPatientPerDay",
                    parseInt(e, 10) || 0,
                  );
                }}
              />
              {errors.maxRequestsPerPatientPerDay &&
                touched.maxRequestsPerPatientPerDay && (
                  <HelperText
                    errorMessage={errors.maxRequestsPerPatientPerDay}
                  />
                )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={`Maximum Other Amount (£${penceToPounds(values.maxOtherAmountPence)}) (*)`}
                error={
                  touched.maxOtherAmountPence && !!errors.maxOtherAmountPence
                }
              />
              <TextField
                name="maxOtherAmountPence"
                useFullWidth={true}
                value={String(values.maxOtherAmountPence)}
                onChange={(e: string) => {
                  updateField("maxOtherAmountPence", parseInt(e, 10) || 0);
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Value in pence. Current: £
                {penceToPounds(values.maxOtherAmountPence)}
              </Typography>
              {errors.maxOtherAmountPence && touched.maxOtherAmountPence && (
                <HelperText errorMessage={errors.maxOtherAmountPence} />
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={`Maximum Mileage Amount (£${penceToPounds(values.maxMileageAmountPence)}) (*)`}
                error={
                  touched.maxMileageAmountPence &&
                  !!errors.maxMileageAmountPence
                }
              />
              <TextField
                name="maxMileageAmountPence"
                useFullWidth={true}
                value={String(values.maxMileageAmountPence)}
                onChange={(e: string) => {
                  updateField("maxMileageAmountPence", parseInt(e, 10) || 0);
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Value in pence. Current: £
                {penceToPounds(values.maxMileageAmountPence)}
              </Typography>
              {errors.maxMileageAmountPence &&
                touched.maxMileageAmountPence && (
                  <HelperText errorMessage={errors.maxMileageAmountPence} />
                )}
            </Box>

            <Box sx={{ mt: "1.5em" }}>
              <Button
                onClick={() => void submitForm()}
                label="Save"
                type="submit"
              />
            </Box>
          </Box>
        );
      }}
    </Formik>
  );
}

export function XeroConfiguration() {
  const [{ data: config, loading }, reload] =
    apiHooks.configuration.getXeroConfiguration();

  return loading || !config ? (
    <CircularProgress />
  ) : (
    <XeroConfigurationInner config={config} reload={reload} />
  );
}
